import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row } from "reactstrap";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../i18/config";
import {
    activateWorkflowScenario,
    deactivateWorkflowScenario,
    getWorkflowFolders,
    getWorkflowScenarios,
    importWorkflowScenario,
    removeWorkflowFolder,
    removeWorkflowScenario,
    runWorkflowScenario,
    saveWorkflowFolder,
    saveWorkflowScenario,
} from "../../api";
import * as route from "../../api/routes";
import BreadCrumb from "../../components/Common/BreadCrumb";
import Loader from "../../components/Loader";
import LeftbarTitle from "../../components/LeftbarTitle";
import MainListFilter from "../../components/MainListFilter";
import RemoveModal from "../../components/RemoveForm";
import { WorkflowFolder, WorkflowScenario } from "./types";
import { ScenarioStatusBadge } from "./StatusBadge";
import { describeSchedule, formatWorkflowDate } from "./format";
import { useModuleCatalog } from "./useModuleCatalog";
import { moduleColor, moduleIcon } from "./moduleStyle";
import UsageBar from "./UsageBar";
import ListCard from "./ui/ListCard";
import WorkflowTable from "./ui/WorkflowTable";
import RowActions, { actionsColumn } from "./ui/RowActions";
import { ConfirmModal, NameModal } from "./ui/Dialogs";

const PER_PAGE = 25;

/** Automation → Scenarios. */
const ScenarioList = () => {
    const { t } = useTranslation(['workflow', 'menu', 'common']);
    const navigate = useNavigate();
    const catalog = useModuleCatalog();
    const fileInput = useRef<HTMLInputElement>(null);

    const [items, setItems] = useState<WorkflowScenario[] | null>(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [folder, setFolder] = useState<string>('');
    const [folders, setFolders] = useState<WorkflowFolder[]>([]);
    const [reloadKey, setReloadKey] = useState(0);

    const [removing, setRemoving] = useState<WorkflowScenario | null>(null);
    const [moving, setMoving] = useState<{ scenario: WorkflowScenario; folder: string } | null>(null);
    const [folderForm, setFolderForm] = useState<{ folder?: WorkflowFolder } | null>(null);
    const [folderRemoving, setFolderRemoving] = useState<WorkflowFolder | null>(null);

    const load = useCallback(() => {
        getWorkflowScenarios({ page, maxResults: PER_PAGE, searchPhrase: search || undefined, folder: folder || undefined })
            .then((r: any) => {
                setItems(r.items || []);
                setTotal(r.meta?.totalCount || 0);
            })
            .catch(() => setItems([]));
    }, [page, search, folder]);

    const loadFolders = useCallback(() => {
        getWorkflowFolders().then((r: any) => setFolders(r.items || [])).catch(() => setFolders([]));
    }, []);

    useEffect(() => load(), [load, reloadKey]);
    useEffect(() => loadFolders(), [loadFolders]);

    const refresh = () => setReloadKey((k) => k + 1);
    const fail = (e: any) => toast.error(e?.message || t('workflow:actionFailed'));
    const open = (s: WorkflowScenario) => navigate(route.AUTOMATION_SCENARIO.replace(':uuid', s.uuid));

    const toggle = (s: WorkflowScenario) =>
        (s.status === 'active' ? deactivateWorkflowScenario(s.uuid) : activateWorkflowScenario(s.uuid))
            .then((r: any) => { toast.success(r.message || t('workflow:saved')); refresh(); })
            .catch((e: any) => {
                if (e?.validation) {
                    toast.error(t('workflow:openToFix'));
                    open(s);

                    return;
                }
                fail(e);
            });

    const run = (s: WorkflowScenario) =>
        runWorkflowScenario(s.uuid)
            .then((r: any) => navigate(route.AUTOMATION_SCENARIO.replace(':uuid', s.uuid) + '?run=' + r.execution))
            .catch(fail);

    const remove = (s: WorkflowScenario) =>
        removeWorkflowScenario(s.uuid).then(refresh).catch(fail).finally(() => setRemoving(null));

    const move = () => moving &&
        saveWorkflowScenario({ uuid: moving.scenario.uuid, folder: moving.folder || null })
            .then(() => { setMoving(null); refresh(); })
            .catch(fail);

    const saveFolder = (name: string) =>
        saveWorkflowFolder({ uuid: folderForm?.folder?.uuid, name })
            .then(() => { setFolderForm(null); loadFolders(); refresh(); })
            .catch(fail);

    const deleteFolder = () => folderRemoving &&
        removeWorkflowFolder(folderRemoving.uuid)
            .then(() => { setFolderRemoving(null); setFolder(''); loadFolders(); refresh(); })
            .catch(fail);

    const selectFolder = (uuid: string) => { setFolder(uuid); setPage(1); };

    const importFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        file.text()
            .then((text) => JSON.parse(text))
            .then((blueprint) => importWorkflowScenario(blueprint, undefined, folder && folder !== 'none' ? folder : null))
            .then((r: any) => {
                toast.success(t('workflow:imported'));
                navigate(route.AUTOMATION_SCENARIO.replace(':uuid', r.uuid));
            })
            .catch((err: any) => toast.error(err instanceof SyntaxError ? t('workflow:invalidJsonFile') : (err?.message || t('workflow:importFailed'))));
    };

    const columns = [
        actionsColumn((s: WorkflowScenario) => (
            <RowActions actions={[
                { icon: 'ri-pencil-fill', label: t('common:edit'), onClick: () => open(s) },
                { icon: 'ri-play-fill', label: t('workflow:runOnce'), onClick: () => run(s), hidden: !s.actions?.canEdit },
                {
                    icon: s.status === 'active' ? 'ri-pause-circle-fill' : 'ri-checkbox-circle-fill',
                    label: s.status === 'active' ? t('workflow:deactivate') : t('workflow:activate'),
                    onClick: () => toggle(s),
                    hidden: !s.actions?.canEdit,
                },
                { icon: 'ri-folder-transfer-fill', label: t('workflow:moveToFolder'), onClick: () => setMoving({ scenario: s, folder: s.folder?.uuid || '' }), hidden: !s.actions?.canEdit || folders.length === 0 },
                'divider',
                { icon: 'ri-delete-bin-fill', label: t('common:delete'), onClick: () => setRemoving(s), hidden: !s.actions?.canRemove, danger: true },
            ]} />
        )),
        {
            name: t('workflow:name'),
            grow: 3,
            cell: (s: WorkflowScenario) => {
                const definition = s.triggerModule ? catalog?.byKey[s.triggerModule] : undefined;

                return (
                    <div className="d-flex align-items-center gap-2 mw-0">
                        <span className="avatar-xs flex-shrink-0">
                            <span className="avatar-title rounded-circle text-white fs-16" style={{ background: moduleColor(s.triggerModule || '') }}>
                                <i className={moduleIcon(definition?.icon)} />
                            </span>
                        </span>
                        <span className="mw-0">
                            <span className="d-block fw-medium text-truncate">{s.name}</span>
                            {s.folder?.name && <span className="d-block text-muted fs-12 text-truncate"><i className="ri-folder-line me-1" />{s.folder.name}</span>}
                        </span>
                    </div>
                );
            },
        },
        { name: t('workflow:status'), cell: (s: WorkflowScenario) => <ScenarioStatusBadge status={s.status} /> },
        { name: t('workflow:schedule'), grow: 2, cell: (s: WorkflowScenario) => describeSchedule(s.schedule, t) },
        { name: t('workflow:lastRun'), grow: 2, cell: (s: WorkflowScenario) => formatWorkflowDate(s.lastRunAt) },
    ];

    const chip = (active: boolean) => 'btn btn-sm ' + (active ? 'btn-primary' : 'btn-light');

    return (
        <>
            <BreadCrumb title={t('menu:automationScenarios')} pageTitle={t('menu:automation')} />

            <Row className="g-4 mb-3">
                <div className="col-sm-auto d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-success" onClick={() => navigate(route.AUTOMATION_SCENARIO.replace(':uuid', 'new'))}>
                        <i className="ri-add-line align-bottom me-1" />{t('workflow:newScenario')}
                    </button>
                    <Link to={route.AUTOMATION_TEMPLATES} className="btn btn-soft-primary"><i className="ri-layout-grid-line align-bottom me-1" />{t('workflow:fromTemplate')}</Link>
                    <button type="button" className="btn btn-soft-secondary" onClick={() => fileInput.current?.click()}><i className="ri-upload-2-line align-bottom me-1" />{t('workflow:importBlueprint')}</button>
                    <input ref={fileInput} type="file" accept="application/json,.json" className="d-none" onChange={importFile} />
                </div>
            </Row>

            <UsageBar reloadKey={reloadKey} />

            <ListCard
                title={t('menu:automationScenarios')}
                filters={<>
                    <MainListFilter value={search} onChange={() => undefined} onClick={(value: string) => { setSearch(value || ''); setPage(1); }} />
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                        <button type="button" className={chip(folder === '')} onClick={() => selectFolder('')}>{t('workflow:allScenarios')}</button>
                        <button type="button" className={chip(folder === 'none')} onClick={() => selectFolder('none')}>{t('workflow:noFolder')}</button>
                        {folders.map((f) => (
                            <div key={f.uuid} className="btn-group btn-group-sm">
                                <button type="button" className={chip(folder === f.uuid)} onClick={() => selectFolder(f.uuid)}>
                                    <i className="ri-folder-line align-bottom me-1" />{f.name}
                                </button>
                                {folder === f.uuid && f.actions?.canEdit && <>
                                    <button type="button" className="btn btn-light" title={t('common:edit')} onClick={() => setFolderForm({ folder: f })}><i className="ri-pencil-line" /></button>
                                    <button type="button" className="btn btn-light text-danger" title={t('common:delete')} onClick={() => setFolderRemoving(f)}><i className="ri-delete-bin-line" /></button>
                                </>}
                            </div>
                        ))}
                        <button type="button" className="btn btn-sm btn-ghost-primary" onClick={() => setFolderForm({})}>
                            <i className="ri-folder-add-line align-bottom me-1" />{t('workflow:newFolder')}
                        </button>
                    </div>
                </>}
            >
                {items === null
                    ? <Loader />
                    : items.length === 0 && !search && !folder
                        ? <div className="text-center text-muted py-5">
                            <i className="ri-flow-chart display-5 d-block mb-2" />
                            <p className="fs-14 mb-0">{t('workflow:noScenarios')}</p>
                        </div>
                        : <WorkflowTable
                            columns={columns as any}
                            data={items}
                            onRowClick={open}
                            paging={{ page, perPage: PER_PAGE, total, onPage: setPage }}
                        />
                }
            </ListCard>

            <RemoveModal
                isOpen={removing !== null}
                removeItem={removing}
                modalTitle={t('common:delete')}
                onRemove={(s: WorkflowScenario) => remove(s)}
                handleClose={() => setRemoving(null)}
            />

            <Modal show={moving !== null} onHide={() => setMoving(null)}>
                <Modal.Header closeButton><LeftbarTitle title={t('workflow:moveToFolder')} /></Modal.Header>
                <Modal.Body>
                    <label className="form-label" htmlFor="wf-move-folder">{moving?.scenario.name}</label>
                    <select id="wf-move-folder" className="form-select" value={moving?.folder || ''} onChange={(e) => moving && setMoving({ ...moving, folder: e.target.value })}>
                        <option value="">{t('workflow:noFolder')}</option>
                        {folders.map((f) => <option key={f.uuid} value={f.uuid}>{f.name}</option>)}
                    </select>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setMoving(null)}>{t('common:cancel')}</Button>
                    <Button variant="primary" onClick={move}>{t('common:save')}</Button>
                </Modal.Footer>
            </Modal>

            <NameModal
                show={folderForm !== null}
                title={folderForm?.folder ? t('common:edit') : t('workflow:newFolder')}
                label={t('workflow:folderName')}
                initial={folderForm?.folder?.name || ''}
                onSave={saveFolder}
                onClose={() => setFolderForm(null)}
            />

            <ConfirmModal
                show={folderRemoving !== null}
                title={t('common:delete')}
                message={t('workflow:confirmDeleteFolder', { name: folderRemoving?.name })}
                confirmLabel={t('common:delete')}
                danger
                onConfirm={deleteFolder}
                onClose={() => setFolderRemoving(null)}
            />
        </>
    );
};

export default ScenarioList;
